import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuth } from "@/composables/useAuth";
import { useUserSettings } from "@/composables/useUserSettings";

// Content types for filter menu
export const detailedContentTypes = [
  { value: "post", label: "Posts", icon: "mdi-post-outline" },
  { value: "video", label: "Videos", icon: "mdi-video-outline" },
  { value: "playlist", label: "Playlists", icon: "mdi-playlist-play" },
];

// Global state for timeline filter (shared between TimelineView and AppHeader)
const selectedDetailedTypes = ref<string[]>([]);
const isLoadingTypes = ref(true);
const isInitialized = ref(false);

// Get all type values for default selection
const allTypeValues = detailedContentTypes.map((t) => t.value);

// User setting key for persisting UI preferences as JSON
const UI_PREFERENCES_SETTING_KEY = "ui_preferences";
// LocalStorage key for fallback (unauthenticated users)
const UI_PREFERENCES_STORAGE_KEY = "ui_preferences";

// Interface for UI preferences stored in user_settings
interface UIPreferences {
  timelineFilterTypes?: string[];
  [key: string]: unknown; // Allow for future UI preferences
}

// Load UI preferences from user_settings (if authenticated) or localStorage (fallback)
function loadUIPreferences(userSettings: ReturnType<typeof useUserSettings>, isAuthenticated: ReturnType<typeof useAuth>["isAuthenticated"]): UIPreferences {
  // Try user_settings first if authenticated
  if (isAuthenticated.value && userSettings.settings.value.length > 0) {
    const setting = userSettings.settings.value.find(s => s.name === UI_PREFERENCES_SETTING_KEY);
    if (setting?.value) {
      try {
        const parsed = JSON.parse(setting.value);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed as UIPreferences;
        }
      } catch (e) {
        console.warn("Failed to parse UI preferences from user_settings", e);
      }
    }
  }

  // Fallback to localStorage for unauthenticated users
  try {
    const stored = localStorage.getItem(UI_PREFERENCES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as UIPreferences;
      }
    }
  } catch (e) {
    console.warn("Failed to load UI preferences from localStorage", e);
  }

  // Return empty object if nothing found
  return {};
}

// Load selected types from UI preferences
// Returns null if no preferences are stored (vs defaults if explicitly empty)
function loadSelectedTypes(userSettings: ReturnType<typeof useUserSettings>, isAuthenticated: ReturnType<typeof useAuth>["isAuthenticated"]): string[] | null {
  const prefs = loadUIPreferences(userSettings, isAuthenticated);
  const types = prefs.timelineFilterTypes;

  if (Array.isArray(types)) {
    // Validate that stored types are valid
    if (types.every((t) => allTypeValues.includes(t))) {
      return types;
    }
  }

  // No stored preferences found - return null to distinguish from "user saved empty"
  return null;
}

// Get selected types with fallback to defaults
function getSelectedTypesOrDefaults(userSettings: ReturnType<typeof useUserSettings>, isAuthenticated: ReturnType<typeof useAuth>["isAuthenticated"]): string[] {
  return loadSelectedTypes(userSettings, isAuthenticated) ?? [...allTypeValues];
}

// Initialize from route query or defaults
export function useTimelineFilter() {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const userSettings = useUserSettings();

  // Initialize only once
  if (!isInitialized.value) {
    const route = router.currentRoute.value;

    // Load from route query if present
    const queryTypes = route.query.types;
    const typesFromQuery = Array.isArray(queryTypes)
      ? queryTypes.join(",")
      : typeof queryTypes === "string"
        ? queryTypes
        : undefined;

    if (typesFromQuery) {
      const newTypes = typesFromQuery.split(",").filter(Boolean);
      selectedDetailedTypes.value = newTypes;
    } else {
      // Load from user_settings/localStorage or use defaults
      const storedTypes = getSelectedTypesOrDefaults(userSettings, isAuthenticated);
      selectedDetailedTypes.value = storedTypes;
    }

    isLoadingTypes.value = false;
    isInitialized.value = true;
  }

  // Watch route changes to update selected types
  watch(
    () => router.currentRoute.value.query.types,
    (newTypes) => {
      const typesFromQuery = Array.isArray(newTypes)
        ? newTypes.join(",")
        : typeof newTypes === "string"
          ? newTypes
          : undefined;

      if (typesFromQuery) {
        const newTypesArray = typesFromQuery.split(",").filter(Boolean);
        selectedDetailedTypes.value = newTypesArray;
      } else if (router.currentRoute.value.name === "timeline") {
        // If on timeline route and no query params, load from user settings
        const storedTypes = getSelectedTypesOrDefaults(userSettings, isAuthenticated);
        selectedDetailedTypes.value = storedTypes;
      }
    }
  );

  // Toggle detailed content type selection
  function toggleDetailedType(type: string) {
    const index = selectedDetailedTypes.value.indexOf(type);
    if (index === -1) {
      selectedDetailedTypes.value = [...selectedDetailedTypes.value, type];
    } else {
      selectedDetailedTypes.value = selectedDetailedTypes.value.filter(t => t !== type);
    }
  }

  return {
    selectedDetailedTypes,
    toggleDetailedType,
    detailedContentTypes,
    isLoadingTypes,
  };
}
